/**
 * A tela de UM grupo — a linha do tempo de entradas e saídas.
 *
 * É o que transforma a contagem em história. A lista de grupos responde "quantos cabem
 * ainda"; aqui se responde **o que aconteceu**: quem entrou, quem saiu, quando, e — o que
 * o produto existe para dizer — **se saiu sozinho ou foi removido pelo admin**.
 *
 * Três coisas que esta tela se recusa a fazer, e que são o motivo de ela existir:
 *
 *  1. **Não trata "descoberto na conferência" como "visto ao vivo".** Cada linha carrega a
 *     origem do fato. Um evento de reconciliação (ADR-0014) significa "percebemos depois
 *     que isso tinha acontecido" — apresentar igual faria o produto mentir sobre a própria
 *     precisão.
 *  2. **Não apresenta os horários como se tivessem a mesma precisão.** Em
 *     `occurredAtSource: 'receipt'` o carimbo é o da nossa descoberta, e a linha diz isso
 *     embaixo do horário (invariante 9).
 *  3. **Não inventa permanência.** `joined_at` é nulo para quem entrou antes do
 *     monitoramento, e a tela escreve "desde antes do monitoramento" — nunca "0 dias",
 *     nunca a data da varredura (ADR-0014, invariante 8).
 *
 * Mobile-first: esta tela se abre em pé, no celular, a partir da lista de grupos — que é
 * item da barra inferior. Por isso o voltar é um alvo grande no topo, a ação principal
 * ocupa a largura toda e a linha do tempo é lista, nunca tabela.
 */
import { useCallback, useMemo, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import {
  Badge,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  NO_DATA_DASH,
  Separator,
  SkeletonText,
  useToast,
} from '@mgdlead/ui';
import {
  ArrowLeft,
  ChevronDown,
  Clock,
  HelpCircle,
  LogIn,
  LogOut,
  Radio,
  Shield,
  ShieldOff,
  Link2Off,
  RefreshCw,
  UserMinus,
  UserPlus,
  UserSearch,
  Users,
} from 'lucide-react';
import { can } from '@mgdlead/shared';

import { FormAlert } from '@mgdlead/ui';
import { PageHeader } from '@mgdlead/ui';
import { CategoryBadge } from './CategoryBadge';
import { ExportContactsCard } from './ExportContactsCard';
import { GroupCategorySelect } from './GroupCategorySelect';
import { LinkCheckHistory } from './LinkCheckHistory';
import { OccupancyBar } from './OccupancyBar';
import {
  GROUP_STATUS,
  INVITE_MISSING,
  INVITE_PRESENT,
  INVITE_WHY_REFRESH,
  groupDisplayName,
  hasInvite,
  inviteRefreshSummary,
  participantsKnown,
  PARTICIPANTS_NEVER_SYNCED,
  syncSummary,
} from '../../../features/group/group-ui';
import {
  EVENT_SOURCE,
  eventHint,
  eventLabel,
  eventTone,
  membershipLine,
  membershipNote,
  PARTICIPANT_CODE_HINT,
  participantLabel,
  reconciledNote,
  TIME_SOURCE,
  type TimelineEventType,
} from '../../../features/group/timeline-ui';
import { RegiaoDoPublico } from '../../../features/regiao/RegiaoDoPublico';
import { useWorkspace } from '../../../features/workspace/WorkspaceContext';
import { useRefreshGroupInvite, useSyncGroupParticipants } from '../../../lib/api';
import {
  useGroup,
  useGroupActivity,
  useGroupTimeline,
  useInvalidateGroupDetail,
  type GroupActivity,
  type TimelineEvent,
} from '../../../lib/api';
import { formatExact, formatRelative } from '../../../lib/datetime';
import { humanizeError } from '../../../lib/errors';
import { useDocumentTitle } from '../../../lib/useDocumentTitle';

/**
 * Ícone por tipo de fato. Fica aqui e não em `timeline-ui.ts` de propósito: aquele arquivo
 * é vocabulário puro, que o teste lê sem montar React.
 */
const EVENT_ICON: Record<TimelineEventType, typeof LogIn> = {
  JOIN: LogIn,
  LEAVE: LogOut,
  PROMOTED: Shield,
  DEMOTED: ShieldOff,
  JOIN_REQUEST: UserPlus,
  SYNC_DISCOVERED: UserSearch,
  SYNC_MISSING: UserMinus,
};

export function GroupDetailPage() {
  const { groupId = '' } = useParams<{ groupId: string }>();
  const { workspace, role } = useWorkspace();
  const { toast } = useToast();

  /**
   * `includeArchived: true`: o grupo que deixou de ser monitorado continua com histórico, e
   * é justamente nele que alguém vai querer olhar o que aconteceu. Esconder a tela de quem
   * arquivou seria esconder a resposta junto com a pergunta.
   */
  const groupQuery = useGroup(workspace.id, groupId);
  const activityQuery = useGroupActivity(workspace.id, groupId);
  const timelineQuery = useGroupTimeline(workspace.id, groupId);
  const syncParticipants = useSyncGroupParticipants();
  const refreshInvite = useRefreshGroupInvite();
  const invalidarDetalhe = useInvalidateGroupDetail();

  const group = groupQuery.data;
  const nome = group ? groupDisplayName(group) : 'Grupo';
  useDocumentTitle(nome);

  const canImport = can(role, 'group:import');
  /**
   * Permissão PRÓPRIA, e não `group:read`: ver a tela mostra códigos pseudônimos; o CSV
   * leva telefone em claro para fora do painel, de onde nenhuma rotação de sal alcança mais.
   */
  const canExportContacts = can(role, 'group:export_contacts');
  /** A mesma permissão que `group.refreshInvite` cobra: quem escreve no grupo. */
  const canUpdateGroup = can(role, 'group:update');

  const eventos = useMemo(
    () => (timelineQuery.data?.pages ?? []).flatMap((page) => page.events),
    [timelineQuery.data],
  );

  /**
   * Sincronizar pessoas é o mesmo gesto em dois lugares: a ação principal do cabeçalho e o
   * "sincronize antes" do card de exportação. Escrito uma vez — duas cópias divergiriam no
   * primeiro ajuste, e uma delas deixaria de invalidar a linha do tempo.
   */
  const podeSincronizar = canImport && group !== undefined && group.archivedAt === null;

  const sincronizarPessoas = useCallback(() => {
    if (group === undefined) return;
    syncParticipants.mutate(
      { workspaceId: workspace.id, monitorId: group.monitorId, groupId: group.id },
      {
        onSuccess: (result) => {
          toast({
            title: result.untrusted ? 'Nada foi alterado' : 'Pessoas sincronizadas',
            description: syncSummary(result),
            tone: result.untrusted ? 'warning' : 'success',
          });
          invalidarDetalhe();
        },
        onError: (error: unknown) =>
          toast({
            title: 'Não deu para sincronizar',
            description: humanizeError(error),
            tone: 'danger',
          }),
      },
    );
  }, [group, invalidarDetalhe, syncParticipants, toast, workspace.id]);

  /**
   * Rebuscar o convite — **e é aqui que este botão mora de verdade.**
   *
   * A tela do grupo é a casa do convite: é dela que se responde "este grupo consegue receber
   * gente?", e é nela que alguém cai quando o grupo para de crescer. A gaveta do
   * redirecionador também oferece o mesmo gesto, porque é lá que o problema costuma ser
   * DESCOBERTO (com o link na mão, antes do anúncio) — dois caminhos para o mesmo ato, e não
   * dois atos: a frase de resultado sai do mesmo `inviteRefreshSummary`.
   */
  const buscarConvite = useCallback(() => {
    if (group === undefined) return;
    refreshInvite.mutate(
      { workspaceId: workspace.id, groupId: group.id },
      {
        onSuccess: (resultado) => {
          // `changed: false` = "o convite continua o mesmo". Boa notícia, tom de sucesso.
          const resumo = inviteRefreshSummary(resultado);
          toast({ title: resumo.title, description: resumo.description, tone: resumo.tone });
          invalidarDetalhe();
        },
        onError: (error: unknown) =>
          toast({
            title: 'Não deu para buscar o convite',
            description: humanizeError(error),
            tone: 'danger',
          }),
      },
    );
  }, [group, invalidarDetalhe, refreshInvite, toast, workspace.id]);

  const voltar = (
    <Link
      to={`/w/${workspace.slug}/grupos`}
      className="inline-flex min-h-11 items-center gap-1.5 self-start rounded-md px-1 text-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <ArrowLeft aria-hidden="true" className="size-4" />
      Grupos
    </Link>
  );

  if (groupQuery.isError) {
    return (
      <>
        {voltar}
        <Card className="mt-3">
          <ErrorState
            title="Não conseguimos abrir este grupo"
            description={humanizeError(groupQuery.error)}
            onRetry={() => void groupQuery.refetch()}
          />
        </Card>
      </>
    );
  }

  return (
    <>
      {voltar}

      <PageHeader
        title={groupQuery.isPending ? 'Carregando…' : nome}
        description={
          group
            ? `Tudo que aconteceu neste grupo, do mais recente para o mais antigo — e de onde cada fato veio.`
            : undefined
        }
        action={
          podeSincronizar ? (
            <Button
              variant="primary"
              leadingIcon={<UserSearch />}
              block
              loading={syncParticipants.isPending}
              loadingLabel="Sincronizando"
              onClick={sincronizarPessoas}
            >
              Sincronizar pessoas
            </Button>
          ) : null
        }
      />

      {/* ------------------------------ cabeçalho ------------------------------ */}
      <GroupSummaryCard
        group={group}
        loading={groupQuery.isPending}
        activity={activityQuery.data}
        timezone={workspace.timezone}
        canRefreshInvite={canUpdateGroup}
        workspaceId={workspace.id}
        refreshingInvite={refreshInvite.isPending}
        onRefreshInvite={buscarConvite}
      />

      {/* ------------------- o seguro contra o grupo ser banido ------------------ */}
      <ExportContactsCard
        workspaceId={workspace.id}
        groupId={groupId}
        timezone={workspace.timezone}
        allowed={canExportContacts}
        syncing={syncParticipants.isPending}
        {...(podeSincronizar ? { onSyncPeople: sincronizarPessoas } : {})}
      />

      {/* ------------------------- de onde é quem está aqui ---------------------- */}
      <div className="mt-3">
        <RegiaoDoPublico periodo="D30" rotuloPeriodo="30 dias" groupId={groupId} />
      </div>

      {/* ---------------------------- linha do tempo --------------------------- */}
      <Card padded={false} className="mt-3">
        <div className="p-3 md:p-4">
          <CardHeader>
            <CardTitle>Linha do tempo</CardTitle>
            <CardDescription>
              Cada linha diz o que aconteceu, com quem, quando — e se nós vimos acontecer ou só
              descobrimos depois.
            </CardDescription>
          </CardHeader>
          <ActivitySummary activity={activityQuery.data} />
        </div>

        {timelineQuery.isPending ? (
          <div className="flex flex-col gap-4 px-3 pb-4 md:px-4">
            <SkeletonText lines={3} />
            <SkeletonText lines={3} />
            <SkeletonText lines={3} />
          </div>
        ) : timelineQuery.isError ? (
          <ErrorState
            title="Não conseguimos carregar a linha do tempo"
            description={humanizeError(timelineQuery.error)}
            onRetry={() => void timelineQuery.refetch()}
          />
        ) : eventos.length === 0 ? (
          <EmptyState
            kind="no-data"
            icon={Clock}
            title="Nenhum fato registrado ainda"
            description={
              group !== undefined && !participantsKnown(group)
                ? 'Este grupo entrou no painel e ainda não passou por uma sincronização de pessoas. Enquanto isso, sabemos quantos são e não sabemos quem são — e nenhuma entrada ou saída pode ser contada.'
                : 'Nada entrou nem saiu deste grupo desde que ele passou a ser monitorado. Quando acontecer, aparece aqui em segundos.'
            }
          />
        ) : (
          <>
            <ul className="flex flex-col divide-y divide-border border-t border-border">
              {eventos.map((event) => (
                <li key={event.id}>
                  <TimelineRow event={event} timezone={workspace.timezone} />
                </li>
              ))}
            </ul>

            <div className="p-3 md:p-4">
              {timelineQuery.hasNextPage ? (
                <Button
                  variant="secondary"
                  leadingIcon={<ChevronDown />}
                  block
                  loading={timelineQuery.isFetchingNextPage}
                  loadingLabel="Carregando"
                  onClick={() => void timelineQuery.fetchNextPage()}
                >
                  Carregar mais
                </Button>
              ) : (
                /**
                 * O fim da lista diz POR QUE acabou. Sem esta frase, quem rolou até embaixo
                 * conclui que o grupo é novo — quando na verdade a história começa no dia em
                 * que ele entrou no painel, e o que veio antes nunca foi nosso.
                 */
                <p className="text-center text-2xs text-muted-foreground">
                  {group
                    ? `Fim da linha do tempo. Só existem fatos a partir de ${
                        formatExact(group.createdAt, workspace.timezone) ??
                        'quando este grupo entrou no painel'
                      }, quando este grupo passou a ser monitorado.`
                    : 'Fim da linha do tempo.'}
                </p>
              )}
            </div>
          </>
        )}
      </Card>
    </>
  );
}

/* ========================================================================== */
/*  Cabeçalho do grupo                                                         */
/* ========================================================================== */

interface SummaryProps {
  group: ReturnType<typeof useGroup>['data'];
  loading: boolean;
  activity: GroupActivity | undefined;
  timezone: string;
  canRefreshInvite: boolean;
  refreshingInvite: boolean;
  onRefreshInvite: () => void;
  workspaceId: string;
}

function GroupSummaryCard({
  group,
  loading,
  activity,
  timezone,
  canRefreshInvite,
  refreshingInvite,
  onRefreshInvite,
  workspaceId,
}: SummaryProps) {
  if (loading || group === undefined) {
    return (
      <Card>
        <SkeletonText lines={3} />
      </Card>
    );
  }

  const spec = GROUP_STATUS[group.status];
  const composicao = membershipNote(activity?.membership ?? null);

  return (
    <Card>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-1">
          <Badge tone={spec.tone} dot title={spec.hint}>
            {spec.label}
          </Badge>
          {!group.isActive && group.status !== 'ARCHIVED' ? (
            <Badge tone="neutral" dot title="Fora do rodízio por escolha sua.">
              Pausado
            </Badge>
          ) : null}
          <Badge tone="outline" icon={<Radio />} title="O número que monitora este grupo.">
            {group.monitorName}
          </Badge>
          {group.categoryName !== null && group.categoryColor !== null && !canRefreshInvite ? (
            <CategoryBadge name={group.categoryName} color={group.categoryColor} />
          ) : null}
          {/* D5: quem pode editar o grupo escolhe a categoria aqui mesmo. */}
          {canRefreshInvite && group.archivedAt === null ? (
            <GroupCategorySelect
              workspaceId={workspaceId}
              groupId={group.id}
              categoryId={group.categoryId}
            />
          ) : null}
        </div>

        <OccupancyBar group={group} />

        {/*
          D4 + pedido do dono (11/10/2026): separar quem administra de quem é membro de verdade.
          Os três números vêm prontos do servidor; `null` é "ainda não sabemos" e vira "—".
        */}
        <ComposicaoDoGrupo group={group} />

        <Separator />

        <ConviteBloco
          group={group}
          canRefresh={canRefreshInvite}
          refreshing={refreshingInvite}
          onRefresh={onRefreshInvite}
        />

        {/* D2: o convite costuma funcionar? Últimas 30 checagens + a frase pronta. */}
        <LinkCheckHistory workspaceId={workspaceId} groupId={group.id} timezone={timezone} />

        <Separator />

        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Fact
            label="Contagem atualizada"
            value={formatRelative(group.lastSyncedAt)}
            exact={formatExact(group.lastSyncedAt, timezone)}
            missing="Nunca — ainda não fomos ver quantos são."
          />
          <Fact
            label="Pessoas varridas"
            value={formatRelative(group.participantsSyncedAt)}
            exact={formatExact(group.participantsSyncedAt, timezone)}
            missing="Nunca — sabemos quantos são, não quem são."
          />
        </dl>

        {/*
          A composição do grupo é o que existe NO LUGAR de "há X dias no grupo". Para a
          maioria das pessoas de um grupo antigo não há data de entrada nenhuma, e essa
          frase é a forma honesta de dizer isso sem inventar número.
        */}
        {composicao !== null ? (
          <p className="text-sm text-muted-foreground">
            <Users aria-hidden="true" className="mr-1 inline size-3.5 align-[-0.125rem]" />
            {composicao}
          </p>
        ) : (
          <FormAlert tone="warning">{PARTICIPANTS_NEVER_SYNCED}</FormAlert>
        )}
      </div>
    </Card>
  );
}

/**
 * **O convite: a única coisa que decide se este grupo recebe alguém.**
 *
 * Ele não aparecia em tela nenhuma, e é o que o allocator exige
 * (`invite_code is not null and invite_revoked_at is null`). Um grupo sem convite fica
 * "Recebendo", com vaga, e não recebe ninguém — o sintoma é a entrada que parou de acontecer.
 *
 * Mostramos o ESTADO (com ícone, não só cor) e o EFEITO, nunca o código: guardamos só o
 * código do convite e ele não é para ser copiado daqui — o destino é montado no servidor
 * (invariante 7). O botão é `secondary` porque a cor de ação desta tela já está gasta em
 * "Sincronizar pessoas" (DESIGN_SYSTEM §1.5), e é de largura cheia no celular (§11).
 */
function ConviteBloco({
  group,
  canRefresh,
  refreshing,
  onRefresh,
}: {
  group: NonNullable<ReturnType<typeof useGroup>['data']>;
  canRefresh: boolean;
  refreshing: boolean;
  onRefresh: () => void;
}) {
  const tem = hasInvite(group);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {tem ? (
          <Badge tone="success" dot title={INVITE_PRESENT}>
            Com convite
          </Badge>
        ) : (
          <Badge tone="warning" icon={<Link2Off />} title={INVITE_MISSING}>
            Sem convite
          </Badge>
        )}
      </div>

      <p className={tem ? 'text-sm text-muted-foreground' : 'text-sm text-warning'}>
        {tem ? INVITE_PRESENT : INVITE_MISSING}
      </p>

      {canRefresh ? (
        <div className="flex flex-col gap-1">
          <Button
            variant="secondary"
            leadingIcon={<RefreshCw />}
            block
            loading={refreshing}
            loadingLabel="Buscando"
            onClick={onRefresh}
            className="md:w-auto md:self-start"
          >
            Buscar convite no WhatsApp
          </Button>
          <span className="text-2xs text-muted-foreground">{INVITE_WHY_REFRESH}</span>
        </div>
      ) : null}
    </div>
  );
}

interface FactProps {
  label: string;
  value: string | null;
  exact: string | null;
  /** O que se diz quando não há data. "Nunca" não é "faz muito tempo" (invariante 8). */
  missing: string;
}

function Fact({ label, value, exact, missing }: FactProps) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-2xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="text-sm">
        {value === null ? (
          <span className="text-muted-foreground">
            <span aria-hidden="true" className="mr-1">
              {NO_DATA_DASH}
            </span>
            {missing}
          </span>
        ) : (
          <span title={exact ?? undefined}>{value}</span>
        )}
      </dd>
    </div>
  );
}

/**
 * Participantes · Administradores · Membros (sem os administradores). Os três vêm do
 * servidor (`participantsCount`, `adminsCount`, `membersCount`) — a tela não subtrai nada
 * (invariante 3). Admins e membros só existem depois da varredura de pessoas: antes dela o
 * servidor manda `null`, e a tela diz que não sabe em vez de mostrar "0" (invariante 8).
 */
function ComposicaoDoGrupo({ group }: { group: NonNullable<ReturnType<typeof useGroup>['data']> }) {
  const semPessoas = group.adminsCount === null || group.membersCount === null;
  return (
    <div className="flex flex-col gap-1.5">
      <dl className="grid grid-cols-3 gap-2" aria-label="Composição do grupo">
        <Numero
          rotulo="Participantes"
          valor={group.participantsCount}
          icon={<Users aria-hidden="true" className="size-3.5" />}
        />
        <Numero
          rotulo="Administradores"
          valor={group.adminsCount}
          icon={<Shield aria-hidden="true" className="size-3.5" />}
        />
        <Numero rotulo="Membros" dica="Sem contar os administradores." valor={group.membersCount} />
      </dl>
      {semPessoas ? (
        <p className="text-2xs text-muted-foreground">
          Administradores e membros aparecem depois de sincronizar as pessoas do grupo.
        </p>
      ) : (
        <p className="text-2xs text-muted-foreground">Membros não inclui os administradores.</p>
      )}
    </div>
  );
}

function Numero({
  rotulo,
  valor,
  dica,
  icon,
}: {
  rotulo: string;
  valor: number | null;
  dica?: string;
  icon?: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 rounded-md border border-border p-2">
      <dt
        className="flex items-center gap-1 truncate text-2xs uppercase tracking-wide text-muted-foreground"
        {...(dica !== undefined ? { title: dica } : {})}
      >
        {icon}
        {rotulo}
      </dt>
      <dd className="nums text-lg font-semibold">
        {valor === null ? (
          <span className="text-muted-foreground" aria-label="sem dados">
            {NO_DATA_DASH}
          </span>
        ) : (
          valor.toLocaleString('pt-BR')
        )}
      </dd>
    </div>
  );
}

/* ========================================================================== */
/*  Resumo: quanto disso nós vimos acontecer                                   */
/* ========================================================================== */

function ActivitySummary({ activity }: { activity: GroupActivity | undefined }) {
  if (activity === undefined) return null;

  const { facts } = activity;
  if (facts.total === 0) return null;

  const conferencia = reconciledNote(facts);

  return (
    <div className="mt-3 flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-1">
        <Badge tone="outline" numeric>
          {facts.total} {facts.total === 1 ? 'fato' : 'fatos'}
        </Badge>
        <Badge tone={EVENT_SOURCE.WEBHOOK.tone} dot numeric title={EVENT_SOURCE.WEBHOOK.hint}>
          {facts.bySource.WEBHOOK} {EVENT_SOURCE.WEBHOOK.label.toLowerCase()}
        </Badge>
        {/*
          A conferência só vira badge quando existe. Um "0 na conferência" fixo na tela
          viraria ruído — e, pior, daria ao número zero a mesma cara de um número medido
          num grupo que nunca foi conferido.
        */}
        {facts.bySource.SYNC > 0 ? (
          <Badge tone={EVENT_SOURCE.SYNC.tone} dot numeric title={EVENT_SOURCE.SYNC.hint}>
            {facts.bySource.SYNC} {EVENT_SOURCE.SYNC.label.toLowerCase()}
          </Badge>
        ) : null}
      </div>

      {conferencia !== null ? (
        <p className="text-2xs text-muted-foreground">{conferencia}</p>
      ) : null}
    </div>
  );
}

/* ========================================================================== */
/*  Uma linha da história                                                      */
/* ========================================================================== */

function TimelineRow({ event, timezone }: { event: TimelineEvent; timezone: string }) {
  const Icon = EVENT_ICON[event.eventType];
  const fonte = EVENT_SOURCE[event.source];
  const hora = TIME_SOURCE[event.occurredAtSource];
  const vinculo = membershipLine(event);
  const relativo = formatRelative(event.occurredAt);

  return (
    <article className="flex flex-col gap-1.5 p-3">
      <div className="flex flex-wrap items-center gap-1">
        <Badge tone={eventTone(event)} icon={<Icon />} title={eventHint(event)}>
          {eventLabel(event)}
        </Badge>
        {/*
          Visto ao vivo × visto na conferência. Este badge é o coração desta tela: sem ele,
          uma saída descoberta três dias depois teria exatamente a mesma cara de uma saída
          que o WhatsApp avisou na hora (ADR-0014).
        */}
        <Badge tone={fonte.tone} dot title={fonte.hint}>
          {fonte.label}
        </Badge>
      </div>

      <p className="text-sm">
        <span className="font-mono" title={PARTICIPANT_CODE_HINT}>
          {participantLabel(event.participant)}
        </span>
        {event.actor !== null ? (
          <span className="text-muted-foreground">
            {' '}
            · por{' '}
            <span className="font-mono" title={PARTICIPANT_CODE_HINT}>
              {participantLabel(event.actor)}
            </span>
          </span>
        ) : null}
      </p>

      <p className="nums flex flex-wrap items-center gap-x-1.5 text-2xs text-muted-foreground">
        <span title={formatExact(event.occurredAt, timezone) ?? undefined}>
          {relativo ?? NO_DATA_DASH}
        </span>
        <span aria-hidden="true">·</span>
        {/*
          A precisão do carimbo, escrita. `receipt` quer dizer "esta é a hora em que
          soubemos", e não a hora do fato — apresentar os dois iguais seria dar a um palpite
          a autoridade de uma medição (invariante 9).
        */}
        <span title={hora.hint} className={hora.approximate ? 'text-warning' : undefined}>
          {hora.approximate ? (
            <HelpCircle aria-hidden="true" className="mr-0.5 inline size-3 align-[-0.0625rem]" />
          ) : null}
          {hora.label}
        </span>
      </p>

      {/*
        O que a tela mostra NO LUGAR de "está há X dias no grupo". Para quem entrou antes do
        monitoramento não existe data de entrada — e `0 dias` seria a resposta errada com
        cara de resposta certa.
      */}
      <p className="text-2xs text-muted-foreground" title={vinculo.hint}>
        {vinculo.joinedAt === null ? (
          vinculo.label
        ) : (
          <>
            {vinculo.label}{' '}
            <span className="nums text-foreground">
              {formatExact(vinculo.joinedAt, timezone) ?? formatRelative(vinculo.joinedAt)}
            </span>
          </>
        )}
      </p>
    </article>
  );
}
